import { gql } from '@apollo/client';

export const DATASHEETS_PAGE_QUERY = gql`
  query datasheets(
    $limit: Int!
    $offset: Int!
    $q: String
    $withDeleted: Boolean
    $orderBy: String
    $orderDirection: String
    $columnFilters: [ColumnFilter!]
  ) {
    datasheets(
      limit: $limit
      offset: $offset
      q: $q
      withDeleted: $withDeleted
      orderBy: $orderBy
      orderDirection: $orderDirection
      columnFilters: $columnFilters
    ) {
      id
      hasDraft
      currentVersion {
        deletedAt
        id
        createdById
        status
        updatedById
        version
        name
        effectiveDate
        notApplicableAfterDate
        defaultResultFamilyId
      }
    }
    datasheetsPageInfo(
      q: $q
      columnFilters: $columnFilters
      withDeleted: $withDeleted
    ) {
      totalCount
    }
  }
`;

export const DATASHEETS_QUERY = gql`
  query datasheets($limit: Int!, $offset: Int!, $q: String, $withDeleted: Boolean) {
    datasheets(limit: $limit, offset: $offset, q: $q, withDeleted: $withDeleted) {
      id
      hasDraft
      currentVersion {
        deletedAt
        id
        createdById
        status
        updatedById
        version
        name
        effectiveDate
        notApplicableAfterDate
        defaultResultFamilyId
        datasheetSections {
          id
          name
          position
          parentPosition
          questions {
            id
            position
            datasheetQuestionId
          }
        }
      }
    }
  }
`;

export const DATASHEET_QUERY = gql`
  query datasheet($id: Int!) {
    datasheet(id: $id) {
      id
      currentVersion {
        deletedAt
        id
        createdById
        status
        updatedById
        version
        name
        effectiveDate
        notApplicableAfterDate
        showToFactoryUser
        defaultResultFamilyId
        isSurvey
        restrictToOneResponse
        allowToResume
        surveySkinId
        languages{
          code
          deletedAt
          id
          name
        }
        datasheetSections {
          id
          name
          position
          parentPosition
          questions {
            id
            position
            datasheetQuestionId
            customQuestionNumber
            validations {
              rule
              text
              position
            }
            conditions {
              action
              predicate
              predicateAddition
              value
              rangeStart
              rangeEnd
              rowName
              logicalOperator
              position
              datasheetQuestionId
            }
          }
        }
      }
    }
  }
`;

export const DATASHEET_VERSIONS_QUERY = gql`
  query datasheetVersions($limit: Int, $offset: Int, $datasheetId: Int!) {
    datasheetVersions(
      limit: $limit
      offset: $offset
      datasheetId: $datasheetId
    ) {
      deletedAt
      datasheetId
      createdAt
      createdById
      id
      updatedAt
      updatedById
      version
      status
      name
      effectiveDate
      notApplicableAfterDate
      showToFactoryUser
      defaultResultFamilyId
      isSurvey
      restrictToOneResponse
      allowToResume
      surveySkinId
      languages{
        code
        deletedAt
        id
        name
      }
      datasheetSections {
        id
        name
        position
        parentPosition
        questions {
          id
          position
          datasheetQuestionId
          validations {
            rule
            text
            position
          }
          conditions {
            action
            predicate
            predicateAddition
            value
            rangeStart
            rangeEnd
            rowName
            logicalOperator
            position
            datasheetQuestionId
          }
        }
      }
    }
  }
`;

export const DATASHEET_PREVIEW_QUERY = gql`
  query datasheetPreview($id: Int!) {
    datasheetPreview(id: $id) {
      id
      currentVersion {
        id
        name
        effectiveDate
        notApplicableAfterDate
        showToFactoryUser
        defaultResultFamilyId
        datasheetSections {
          id
          name
          position
          parentPosition
          questions {
            id
            position
            questionNumber
            datasheetQuestionId
            datasheetQuestion {
              id
              currentVersion {
                id
                createdById
                status
                updatedById
                version
                text
                questionType
                descriptionAlias
                descriptionExternal
                numberSettings {
                  id
                  precision
                  removeDecimalLimitation
                }
                autofillSettings {
                  id
                  field
                }
                radioButtonSettings {
                  multipleAnswer
                  rows
                }
                relationSettings {
                  id
                  entity
                  entityItems
                  allowMultiSelect
                }
                fileSettings {
                  id
                  fileType
                  fileSize
                }
                dropdownSettings {
                  id
                  options {
                    definition
                    value
                  }
                  allowMultiSelect
                }
                dependentDropdownSettings {
                  id
                  dependsOn
                  allowMultiSelect
                  options {
                    definition
                    label
                    parent
                  }
                }
                tableSettings {
                  id
                  columns {
                    number
                    name
                    type
                    precision
                    removeDecimalLimitation
                    fileType
                    fileSize
                    currencies
                    currencyItems {
                      id
                      name
                      code
                      symbol
                    }
                    entity
                    entityItems
                    allowMultiSelect
                    options {
                      definition
                      value
                    }
                    dependsOn
                    dependentDropdownOptions {
                      definition
                      label
                      parent
                    }
                  }
                  enableMultipleRows
                  maximizeWidth
                  fixedValues {
                    columnName
                    value
                  }
                }
                currencySettings {
                  id
                  currencies
                  currencyItems {
                    id
                    name
                    code
                    symbol
                  }
                }
                smileyRatingSettings {
                  available
                  text
                }
                starRatingSettings{
                  id
                  numberOfStarsAvailable
                  rows
                }
                translations {
                  id
                  languageId
                  text
                  descriptionAlias
                  options
                }
              }
            }
            validations {
              rule
              text
              position
            }
            conditions {
              id
              action
              predicate
              predicateAddition
              value
              rangeStart
              rangeEnd
              rowName
              logicalOperator
              position
              datasheetQuestionId
            }
          }
        }
      }
    }
  }
`;

export const DATASHEET_VERSION_QUERY = gql`
  query datasheetVersionEffective($id: Int!, $versionId: Int!) {
    datasheetVersionEffective(id: $id, versionId: $versionId) {
      id
      name
      effectiveDate
      notApplicableAfterDate
      showToFactoryUser
      defaultResultFamilyId
      isSurvey
      restrictToOneResponse
      allowToResume
      surveySkinId
      defaultResultFamily {
        currentVersion {
          id
          name
          code
          resultFamilyId
          resultFamilyOutcomes {
            id
            order
            result {
              id
              currentVersion {
                code
                createdAt
                description
                id
                resultCategoryId
                resultId
                status
              }
            }
            resultCategory {
              id
              currentVersion {
                code
                id
                name
                resultCategoryId
                status
              }
            }
            resultCategoryId
            resultFamilyVersionId
            resultId
          }
          status
          version
        }
      }
      languages {
        id
        name
      }
      datasheetSections {
        id
        name
        position
        parentPosition
        questions {
          id
          position
          questionNumber
          datasheetQuestionId
          datasheetQuestion {
            id
            currentVersion {
              id
              createdById
              status
              updatedById
              version
              text
              questionType
              descriptionAlias
              descriptionExternal
              numberSettings {
                id
                precision
                removeDecimalLimitation
              }
              autofillSettings {
                id
                field
              }
              radioButtonSettings {
                multipleAnswer
                rows
              }
              relationSettings {
                id
                entity
                entityItems
                allowMultiSelect
              }
              fileSettings {
                id
                fileType
                fileSize
              }
              dropdownSettings {
                id
                options {
                  definition
                  value
                }
                allowMultiSelect
              }
              dependentDropdownSettings {
                id
                dependsOn
                allowMultiSelect
                options {
                  definition
                  label
                  parent
                }
              }
              tableSettings {
                id
                columns {
                  number
                  name
                  type
                  precision
                  removeDecimalLimitation
                  fileType
                  fileSize
                  currencies
                  currencyItems {
                    id
                    name
                    code
                    symbol
                  }
                  entity
                  entityItems
                  allowMultiSelect
                  options {
                    definition
                    value
                  }
                  dependsOn
                  dependentDropdownOptions {
                    definition
                    label
                    parent
                  }
                }
                enableMultipleRows
                maximizeWidth
                fixedValues {
                  columnName
                  value
                }
              }
              currencySettings {
                id
                currencies
                currencyItems {
                  id
                  name
                  code
                  symbol
                }
              }
              smileyRatingSettings {
                available
                text
              }
              starRatingSettings{
                id
                numberOfStarsAvailable
                rows
              }
              textSettings {
                id
                isMaximized
                isTextarea
              }
              translations {
                id
                languageId
                text
                descriptionAlias
                options
              }
            }
          }
          validations {
            rule
            text
            position
          }
          conditions {
            id
            action
            predicate
            predicateAddition
            value
            rangeStart
            rangeEnd
            rowName
            logicalOperator
            position
            datasheetQuestionId
          }
        }
      }
    }
  }
`;
